































































import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop} from 'vue-property-decorator';
import BindAttrs from '@/utils/binds-attrs';
import {AMETRAS_MIME_LOOKUP_INST} from '@/utils/mime.service';
import {HTTP_SERVICE_INSTANCE} from '@/services/http.service';
import AmetrasLabelTemplate from '@/fields/AmetrasLabelTemplate.vue';
import {DisguiseBusinessExceptionWrapper} from '../../exception/disguise-business-exception-wrapper';
import {catchError, from, mergeMap, Observable, of} from 'rxjs';
import {compress} from 'image-conversion';
import {UPLOAD_PREFERENCES_SERVICE_INSTANCE} from '@/services/upload-preferences.service';
import {ERROR_SERVICE_INSTANCE} from '@/services/error.service';
import {ROUTER_SERVICE} from '@/services/router.service';
import AdvancedConfirmDialog, {ConfirmationDialogButton, ConfirmationDialogConfig} from '@/views/AdvancedConfirmDialog.vue';

interface UploadFile {
  name: string;
  progress: number;
  indeterminate: boolean;
  uploaded: boolean;
  id?: string;
  icon: string;
}

/**
 * General Component for Fields of type Upload
 *
 */
@Component({
  components: {AdvancedConfirmDialog, AmetrasLabelTemplate},
  mixins: [BindAttrs]
})
export default class AmetrasUploadFieldTemplate extends Vue {
  private static readonly SUPPORTED_PICTURE_COMPRESSION_MIME_TYPES: string[] = ['image/png', 'image/jpeg', 'image/gif'];

  @Prop({
    required: true
  }) max!: number;
  @Prop() label!: string;
  @Prop() readonly!: boolean;
  // this is your binding service url. it must contain a token {uploadUuid}
  @Prop({
    required: true,
    validator(value: string): boolean {
      return value.indexOf('{uploadUuid}') >= 0;
    }
  }) value!: string;

  @Prop() typeSpecificSettings!: DisguiseDto.FileUploadFieldSpecificSettings;

  dragOver: boolean = false;
  existingFilesLoading: boolean = false;
  readableMaxSize: string = '';
  files: UploadFile[] = [];

  confirmationDialogConfig: ConfirmationDialogConfig | null = null;

  $refs!: {
    selectFile: HTMLInputElement
  };

  created() {
    let readableMaxSize = '';
    if (this.max > 1024 * 1024) {
      readableMaxSize = Math.round(this.max / (1024 * 1024)) + ' MB';
    } else if (this.max > 1024) {
      readableMaxSize = Math.round(this.max / (1024)) + ' KByte';
    } else {
      readableMaxSize = Math.round(this.max) + ' Byte';
    }
    this.readableMaxSize = readableMaxSize;

    this.existingFilesLoading = true;
    HTTP_SERVICE_INSTANCE.getAxios().get<DisguiseDto.FileUploadDto[]>(
        this.value.replace('/{uploadUuid}', '')
    ).then((response) => {
      const existingUploads: UploadFile[] = [];

      for (const currentExistingUpload of response.data || []) {
        const fileIcon = AMETRAS_MIME_LOOKUP_INST.lookupIcon(currentExistingUpload.fileExtension || '');
        existingUploads.push({
          progress: 100,
          uploaded: true,
          name: currentExistingUpload.fileName || '',
          icon: fileIcon,
          id: currentExistingUpload.uuid,
          indeterminate: false
        });
      }

      this.files = this.files.concat(existingUploads);
      this.existingFilesLoading = false;
    }, (error) => {
      if (!(error instanceof DisguiseBusinessExceptionWrapper)) {
        this.$store.commit('showSnack', {
          message: `Fehler beim Laden existierender Anhänge.`,
          color: 'error'
        });
      }
      this.existingFilesLoading = false;
    });
  }

  /**
   * The user selected files via file chooser
   */
  fileSelected() {
    const selectedFiles: FileList | null = this.$refs.selectFile.files;
    if (selectedFiles) {
      this.uploadFiles(selectedFiles);
    }
  }

  /**
   * The user dropped files via DnD
   */
  dropFiles(dropEvent: DragEvent) {
    const droppedFiles: FileList | undefined = (dropEvent.dataTransfer || {}).files;
    if (droppedFiles) {
      this.uploadFiles(droppedFiles);
    }
    this.dragOver = false;
  }

  /**
   * The user drags files over the file drop area
   */
  dragover(dropEvent: DragEvent) {
    this.dragOver = true;
  }

  /**
   * The user exits dragging files over the file drop area
   */
  dragexit(dropEvent: DragEvent) {
    this.dragOver = false;
  }

  /**
   * The user leaves the file drop area
   */
  dragleave(dropEvent: DragEvent) {
    this.dragOver = false;
  }

  /**
   * The user pressed delete button
   */
  deleteFile(file: UploadFile, event: Event) {
    event.preventDefault();

    this.confirmationDialogConfig = {
      title: 'Bestätigen',
      headerColor: 'primary',
      message: `Möchten Sie den Anhang \'${file.name}\' wirklich löschen?`,
      buttons: [{
        text: 'Bestätigen',
        title: 'Bestätigen',
        disabled: false,
        loading: false,
        autoFocus: true,
        onClick: (button: ConfirmationDialogButton) => {
          button.disabled = true;
          button.loading = true;
          file.uploaded = false;
          file.indeterminate = true;
          file.progress = 0;
          HTTP_SERVICE_INSTANCE.getAxios().delete(
              this.value.replace('{uploadUuid}', file.id || '')
          ).then(() => {
            this.files.splice(this.files.indexOf(file), 1);

            this.$store.commit('showSnack', {
              message: `\'${file.name}\' wurde gelöscht.`,
              color: 'success'
            });
            this.confirmationDialogConfig = null;
            this.$emit('afterUploadDeleted');
          }, (error) => {
            if (!(error instanceof DisguiseBusinessExceptionWrapper)) {
              this.$store.commit('showSnack', {
                message: `Löschen der Datei \'${file.name}\' fehlgeschlagen`,
                color: 'error'
              });
            }
            file.uploaded = true;
            button.disabled = false;
            button.loading = false;
          });
        }
      }, {
        text: 'Abbrechen',
        title: 'Abbrechen',
        disabled: false,
        loading: false,
        onClick: () => {
          this.confirmationDialogConfig = null;
        }
      }],
      onEsc: () => {
        this.confirmationDialogConfig = null;
      }
    };
  }

  /**
   * Internal upload handling
   */
  private uploadFiles(files: FileList) {
    for (const currentFile of files || []) {
      const fileExtension = AMETRAS_MIME_LOOKUP_INST.extractExtension(currentFile.name);
      const fileIcon = AMETRAS_MIME_LOOKUP_INST.lookupIcon(fileExtension);

      this.compressIfPossible(currentFile).subscribe((compressedFile) => {
        if (compressedFile.size > this.max) {
          this.$store.commit('showSnack', {
            message: `Die Datei \'${currentFile.name}\' überschreitet die zulässige Dateigröße.`,
            color: 'error'
          });
        } else {
          const newUploadFile: UploadFile = {
            name: currentFile.name,
            progress: 0,
            uploaded: false,
            indeterminate: false,
            icon: fileIcon
          };
          this.files.push(newUploadFile);

          const formData: FormData = new FormData();
          formData.append('file', compressedFile, currentFile.name);

          HTTP_SERVICE_INSTANCE.getAxios().request<string>({
            method: 'post',
            url: (this.typeSpecificSettings ? this.typeSpecificSettings.uploadServiceUrl : '/upload/api/v0/files'),
            data: formData,
            headers: {
              Accept: 'text/plain',
            },
            onUploadProgress: (progressEvent: ProgressEvent) => {
              newUploadFile.progress = Math.round(progressEvent.loaded * 100 / progressEvent.total);
            }
          }).then((response) => {
            const uploadUuid = response.data;
            newUploadFile.progress = 100;
            newUploadFile.indeterminate = true;
            newUploadFile.id = uploadUuid;
            HTTP_SERVICE_INSTANCE.getAxios().put(this.value.replace('{uploadUuid}', uploadUuid)).then(() => {
              newUploadFile.uploaded = true;
              this.$emit('afterUploadCompleted', newUploadFile);
            }, (error) => {
              if (!(error instanceof DisguiseBusinessExceptionWrapper)) {
                this.$store.commit('showSnack', {
                  message: `Verarbeitung der Datei \'${newUploadFile.name}\' fehlgeschlagen`,
                  color: 'error'
                });
              }
              this.files.splice(this.files.indexOf(newUploadFile), 1);
            });
          }, (error) => {
            if (!(error instanceof DisguiseBusinessExceptionWrapper)) {
              this.$store.commit('showSnack', {
                message: `Dateiupload der Datei \'${newUploadFile.name}\' fehlgeschlagen`,
                color: 'error'
              });
            }
            this.files.splice(this.files.indexOf(newUploadFile), 1);
          });
        }
      });
    }
  }

  private compressIfPossible(file: Blob): Observable<Blob> {
    let result: Observable<Blob> = of(file);
    if (AmetrasUploadFieldTemplate.SUPPORTED_PICTURE_COMPRESSION_MIME_TYPES.indexOf(file.type) > -1) {
      result = UPLOAD_PREFERENCES_SERVICE_INSTANCE.getUploadPreferencesCached().pipe(
          mergeMap((settings) => {
            return from(compress(file, {
              quality: settings.quality,
              width: settings.maxPixelWidth || undefined,
              height: settings.maxPixelHeight || undefined
            }));
          }),
          catchError((error) => {
            ERROR_SERVICE_INSTANCE.reportError({
              message: 'Cannot fetch upload preferences, due to ...' + error,
              route: ROUTER_SERVICE.getInstance().currentRoute.fullPath,
              name: 'Compress Error',
              stack: '',
              timestamp: new Date().toISOString()
            });
            return of(file);
          })
      );
    }
    return result;
  }
}
